<template>
  <div id="users" class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between align-center">
      <h1 class="text-xl text_color font-semibold">Mailboxes</h1>
      <div class="flex gap-2">
        <a-button  class="secondary_medium" @click="isExportMailboxDrawerVisible = true">
          <template #icon>
            <ThemeImage :icon="imageNames.EXPORT_MAILBOX_ICON" class="size-4" />
          </template>
        </a-button>
        <a-button
          v-if="isInfraForgeMailboxesEnabled"
          class="secondary_medium"
          @click="navigateToGenerateMailboxes"
        >
          Generate Mailboxes
        </a-button>
        <a-dropdown placement="bottomRight" :trigger="['click']">
          <a-button class="primary_medium"> Connect Mailbox </a-button>
          <template #overlay>
            <a-menu class="dropdown-mailboxes">
              <a-menu-item
                class="dropdown-item-mailboxes"
                v-for="vendor in emailVendorOptions"
                :key="vendor"
                @click="connectMailbox(vendor)"
              >
                <div class="dropdown-item">
                  <ThemeImage
                    :icon="emailVendorIcons[vendor]"
                    alt="email_vendor_icons"
                    class="size-[16px]"
                  />
                  <span class="proxima_nova text-base text_color">
                    <template v-if="vendor === 'SMTP'"> SMTP </template>
                    <template v-else>
                      {{ toTitleCase(vendor) }}
                    </template>
                  </span>
                </div>
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </div>
    </div>

    <div class="flex flex-wrap items-center justify-start gap-[24px]">
      <div
        class="flex items-center space-x-2"
        v-for="tracking in trackingOptions"
        :key="tracking.type"
      >
        <a-switch
          v-model:checked="tracking.enabled"
          size="small"
          @change="() => handleTrackingToggle(tracking.type, tracking.enabled)"
        />
        <span class="text-[13px] secondary_color text-base ml-2 text_color">
          Enable {{ tracking.label }} tracking
        </span>
      </div>
    </div>

    <CustomTable
      v-if="sharedMailboxes.length > 0"
      :headers="mailboxHeaders"
      :data="sharedMailboxes"
      :custom-column-render="customColumnRenderWithProps"
      :show-actions-column="true"
      :actions="secondaryActions"
      prefixImageBorderRadius="0%"
      :key="tableKey"
    />
  </div>

  <smtp-config
    v-if="isOtherMailboxDrawerVisible"
    v-model:drawerVisible="isOtherMailboxDrawerVisible"
    @update:isOtherMailboxDrawerVisible="
      (val) => (isOtherMailboxDrawerVisible = val) && (editingMailboxId = null)
    "
    :email-account-id="editingMailboxId"
    @smtpMailBoxAdded="() => fetchMailboxes()"
    :is-team-mailbox="true"
  />
  <export-mailbox-drawer
    v-if="isExportMailboxDrawerVisible"
    v-model:drawerVisible="isExportMailboxDrawerVisible"
    @update:drawerVisible="isExportMailboxDrawerVisible = $event"
  />
</template>

<script setup>
import { ref, onMounted, inject, computed } from "vue";
import DashboardService from "@/src/api/DashboardService.js";
import IntegrationService from "@/src/api/IntegrationService.js";
import { toTitleCase } from "@/src/utils/utils.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import { emailVendorIcons } from "@/src/constants/settingConstants.js";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";
import ExportMailboxDrawer from "@/src/components/mailboxes_components/ExportMailboxDrawer.vue";

import { useRouter } from "vue-router";
import SmtpConfig from "@/src/components/SmtpConfig.vue";
import { createCSVImportCallback } from "@/src/utils/csvBoxUtility.js";
import {
  MAILBOX_HEADERS,
  EMAIL_VENDOR_OPTIONS,
  getTrackingOptions,
  SECONDARY_ACTIONS,
  CSV_BOX_SHEET_KEYS,
} from "@/src/constants/mailboxesConstants.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { useSmtpReconnect } from "@/src/composables/useMailbox.js";

const router = useRouter();
const openTrackingEnabled = ref(false);
const clickTrackingEnabled = ref(false);
const sharedMailboxes = ref([]);
const tableKey = ref(0);

const trackingOptions = computed(() =>
  getTrackingOptions(openTrackingEnabled, clickTrackingEnabled)
);
const mailboxHeaders = ref(MAILBOX_HEADERS);
const editingMailboxId = ref(null);
const isOtherMailboxDrawerVisible = ref(false);
const isExportMailboxDrawerVisible = ref(false);

const isInfraForgeMailboxesEnabled = computed(() => {
  return !!localStorage.getItem("infra_forge_mailboxes");
});

const { customColumnRenderWithProps } = useSmtpReconnect(
  editingMailboxId,
  isOtherMailboxDrawerVisible
);

const secondaryActions = [
  {
    ...SECONDARY_ACTIONS[0],
    handler: (row) => manageSignature(row),
  },
  {
    ...SECONDARY_ACTIONS[1],
    handler: (row) => markAsPersonal(row),
  },
  {
    ...SECONDARY_ACTIONS[2],
    handler: (row) => disconnectMailbox(row),
    condition: (row) => row.email.status === "CONNECTED",
  },
  {
    label: "Delete",
    handler: (row) => deleteMailbox(row),
    icon: imageNames.DELETE_GREY,
    confirmRequired: true,
    confirmTitle: "Delete Mailbox",
    confirmMessage: "Are you sure you want to delete this mailbox?",
    condition: (row) => row.email.status === "DISCONNECTED",
  },
];

const manageSignature = (row) => {
  router.push({ name: "mailbox-signature", params: { id: row.email.id } });
};

const markAsPersonal = async (row) => {
  try {
    const response = await IntegrationService.setEmailAccountSharing(row.email.id, false);
    if (response.data.success) {
      const index = sharedMailboxes.value.findIndex(
        (mailbox) => mailbox.email.id === row.email.id
      );
      if (index !== -1) {
        sharedMailboxes.value.splice(index, 1);
      }
      await fetchMailboxes();
      message.success("Mailbox successfully moved to your personal mailbox");
    } else {
      message.error(response.data.error || "Failed to mark mailbox as personal");
    }
  } catch (error) {
    console.error("Error marking mailbox as personal:", error);
    if (error.response?.status === 404) {
      message.error(error.response?.data?.error || "You cannot move this email to the personal mailbox because you’re not the owner.");
    } else {
      message.error("An error occurred while marking mailbox as personal");
    }
  }
};

const disconnectMailbox = async (row) => {
  try {
    const payload = {
      vendor: row.email.vendor,
      primary: false,
      email: row.email.text,
    };

    const response = await DashboardService.disconnectMailbox(payload);
    if (response.success) {
      message.success("Mailbox disconnected successfully");
      await fetchMailboxes();
    } else {
      message.error("Failed to disconnect mailbox");
    }
  } catch (error) {
    message.error("An error occurred while disconnecting the mailbox");
  }
};

const deleteMailbox = async (row) => {
  try {
    const response = await DashboardService.deleteMailbox(row.email.id);
    if (response.success) {
      message.success("Mailbox deleted successfully");
      await fetchMailboxes();
    } else {
      message.error(response.error || "Failed to delete mailbox");
    }
  } catch (error) {
    console.error("Error deleting mailbox:", error);
    message.error("An error occurred while deleting the mailbox");
  }
};

const getSharedMailboxesTableData = (senderDetails) => {
  return senderDetails
    .filter(
      (mailbox) =>
        !mailbox.is_primary ||
        mailbox.is_primary === 0 ||
        mailbox.is_primary === false
    )
    .map((sender) => {
      return {
        email: {
          text: sender.email,
          prefixImage: emailVendorIcons[sender.vendor],
          status: sender.status,
          vendor: sender.vendor,
          id: sender.id,
          is_purchased: sender.is_purchased,
        },
      };
    });
};

const emailVendorOptions = ref(EMAIL_VENDOR_OPTIONS);

const fetchMailboxes = async () => {
  try {
    const response = await DashboardService.fetchSharedMailboxes();
    if (response.success) {
      sharedMailboxes.value = getSharedMailboxesTableData(
        response.email_accounts
      );
      tableKey.value += 1;
    }
  } catch (error) {
    message.error("Error fetching mailboxes");
  }
};

const connectMailbox = async (vendor) => {
  try {
    if (vendor === "SMTP") {
      editingMailboxId.value = null;
      isOtherMailboxDrawerVisible.value = true;
      return;
    }
    if (vendor === "BULK_UPLOAD") {
      openCSVImport();
      return;
    }
  } catch (error) {
    message.error("Error connecting mailbox");
  }
};

const handleTrackingToggle = async (trackingType, newState) => {
  const trackingMap = {
    open: {
      toggleFunction: DashboardService.setOpenTracking,
      state: openTrackingEnabled,
    },
    click: {
      toggleFunction: DashboardService.setClickTracking,
      state: clickTrackingEnabled,
    },
  };

  try {
    const { toggleFunction, state } = trackingMap[trackingType];
    const response = await toggleFunction(!newState);

    if (response.success) {
      state.value = !response[`${trackingType}_tracking_enabled`];

      message.success(
        `${capitalizeFirstLetter(trackingType)} tracking ${
          state.value ? "enabled" : "disabled"
        }`
      );
    } else {
      state.value = !newState;
      message.error(`Failed to update ${trackingType} tracking setting`);
    }
  } catch (error) {
    trackingMap[trackingType].state.value = !newState;
    message.error(
      `An error occurred while updating ${trackingType} tracking setting`
    );
  }
};

const capitalizeFirstLetter = (string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

const fetchTrackingState = async (trackingType) => {
  try {
    const trackingMap = {
      open: {
        fetchFunction: DashboardService.fetchOpenTrackingState,
        state: openTrackingEnabled,
      },
      click: {
        fetchFunction: DashboardService.fetchClickTrackingState,
        state: clickTrackingEnabled,
      },
    };

    const { fetchFunction, state } = trackingMap[trackingType];

    const response = await fetchFunction();
    if (response.success) {
      state.value = !response[`${trackingType}_tracking_enabled`];
    }
  } catch (error) {
    message.error(`Failed to fetch ${trackingType} tracking state`);
  }
};

const csvboxSheetKey =
  process.env.NODE_ENV === "development" || process.env.NODE_ENV === "staging"
    ? CSV_BOX_SHEET_KEYS.development
    : CSV_BOX_SHEET_KEYS.production;

const csvBoxState = inject("csvBoxState");

const setupCSVImporter = async () => {
  try {
    await csvBoxState.initializeCSVModal();
    const importCallback = createCSVImportCallback(
      "mailbox",
      DashboardService.importViaCsvbox,
      (successMessage) => {
        message.success(successMessage);
      },
      (errorMessage) => message.error(errorMessage)
    );
    csvBoxState.createImporter(csvboxSheetKey, {}, importCallback);
  } catch (error) {
    message.error("Failed to initialize CSV importer. Please try again later.");
  }
};

const openCSVImport = () => {
  csvBoxState.openCSVImport();
};

const customRow = (record) => {
  return {
    onClick: () => {
      router.push({
        name: "mailbox-signature",
        params: { id: record.email.id },
      });
    },
  };
};

const navigateToGenerateMailboxes = () => {
  router.push("/settings/mailboxes/generate");
};

onMounted(() => {
  fetchMailboxes();
  fetchTrackingState("open");
  fetchTrackingState("click");
  const route = useRoute();
  const queryParams = route.query;
    if (queryParams && queryParams.success && queryParams.success === "true") {
    message.success("Mailbox connected successfully");
  } else if (
    queryParams &&
    queryParams.permissions &&
    queryParams.permissions === "false"
  ) {
    message.error(
      "Mailbox couldn't be connected because of insufficient permission."
    );
  } else if (
    queryParams &&
    queryParams.duplicate_email &&
    queryParams.duplicate_email === "true"
  ) {
    message.error("Email already connected in this workspace by another team member");
  } else if (
    queryParams &&
    queryParams.success &&
    queryParams.success === "false"
  ) {
    const errorMessage = queryParams.error 
      ? decodeURIComponent(queryParams.error.replace(/\+/g, ' '))
      : "Failed to connect mailbox";
    message.error(errorMessage);
  }
  setupCSVImporter();
});
</script>
<style scoped>
.dropdown-mailboxes {
  background: var(--dropdown-bg);
}

.dropdown-item-mailboxes {
  color: var(--text-color);
}

.dropdown-item-mailboxes:hover {
  background: var(--white-opacity-8);
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  padding: 6px 12px;
  border-radius: 8px;
}
</style>
